import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";

/**
 * Returns the current user's team_id for use in insert operations.
 * All team-scoped data must include team_id when inserting.
 */
export function useTeamId() {
  const { user } = useAuth();

  const { data: teamId, isLoading } = useQuery({
    queryKey: ["user-team-id", user?.id],
    enabled: !!user,
    staleTime: 10 * 60 * 1000, // 10 min cache
    queryFn: async () => {
      const { data, error } = await supabase
        .from("team_members")
        .select("team_id")
        .eq("user_id", user!.id)
        .eq("is_active", true)
        .single();
      if (error) throw error;
      return data?.team_id as string;
    },
  });

  return { teamId: teamId || null, isLoading };
}

/**
 * Helper to get team_id imperatively (for use in mutation functions).
 */
export async function getTeamId(): Promise<string> {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) throw new Error("Não autenticado");

  const { data, error } = await supabase
    .from("team_members")
    .select("team_id")
    .eq("user_id", user.id)
    .eq("is_active", true)
    .single();

  if (error || !data?.team_id) throw new Error("Equipa não encontrada");
  return data.team_id as string;
}
